import { z } from 'zod';
import { AiProvenanceSchema } from './ai-provenance';
import { ChatEntityRefSchema } from './chat-entity';
import { CHAT_UPLOAD_MEDIA_TYPES, MAX_CHAT_ATTACHMENT_FILENAME_LENGTH, STORED_CHAT_UPLOAD_KEY_REGEX } from './chat-attachment';
/**
 * App-owned chat message envelope, shared by every chat agent (app chat,
 * Bliksy). This is deliberately NOT the AI SDK `UIMessage` shape: only an
 * allowlisted set of parts is ever persisted or returned, so provider metadata,
 * reasoning traces and large payloads can never reach Mongo or the browser.
 *
 * Three projections exist for a message (plan 009 §6.1) and only the first two
 * live here:
 *  1. storage / read envelope — this file;
 *  2. history UI parts — this file (same shape; the frontend renders `parts`);
 *  3. model text projection — backend-only, never these typed parts.
 */ /**
 * Rows written before typed parts existed. They carry `content` only, so their
 * parts are derived at read time and their `id` is synthesized deterministically
 * from position (plan 009 §6.5). Never written by new code.
 */ export const CHAT_MESSAGE_SCHEMA_VERSION_LEGACY = 0;
/**
 * Current envelope: `text`, attached documents (`file`) and completed rich tool
 * parts (`tool-renderChart`, `tool-generateDocument`, `tool-renderEntityView`).
 * The version is NOT bumped per new part type — the reader validates each part
 * on its own and skips what it does not know, so an added type is
 * forward-compatible by construction (see §6.4 below).
 */ export const CHAT_MESSAGE_SCHEMA_VERSION_V1 = 1;
/** Versions this reader can interpret a persisted `parts` array under. */ export const KNOWN_CHAT_MESSAGE_SCHEMA_VERSIONS = [
    CHAT_MESSAGE_SCHEMA_VERSION_LEGACY,
    CHAT_MESSAGE_SCHEMA_VERSION_V1
];
/**
 * Roles a chat ENVELOPE can carry. Deliberately narrower than the agents' own
 * role enums (Bliksy's persisted roles): a `system`
 * turn is prompt input and a `tool` turn is audit data — neither is a
 * renderable, replayable message — so they never become an envelope. Do not
 * "unify" this with the agent enums; the narrowing is the point.
 */ export const CHAT_MESSAGE_ROLES = [
    'user',
    'assistant'
];
export const ChatMessageRoleSchema = z.enum(CHAT_MESSAGE_ROLES);
/**
 * Bound on ids so an unbounded client-supplied string can never become a Mongo
 * key or a log line of arbitrary size.
 */ export const MAX_CHAT_MESSAGE_ID_LENGTH = 128;
/** Prefix of the deterministic id a row written before plan 009 is read under. */ export const LEGACY_CHAT_MESSAGE_ID_PREFIX = 'legacy:';
/** Prefix of the id a row gets when neither it nor its reader supplied one. */ export const SYNTHETIC_CHAT_MESSAGE_ID_PREFIX = 'chat-message:';
/**
 * Id prefixes the SERVER mints. A client-supplied turn key must never land in
 * this namespace: the key decides which stored turn a write replaces and which
 * one replay hides, and a client naming a server-minted id would make those two
 * sides disagree about the same session (a turn hidden from the model but
 * appended rather than replaced in storage).
 */ export const RESERVED_CHAT_MESSAGE_ID_PREFIXES = [
    LEGACY_CHAT_MESSAGE_ID_PREFIX,
    SYNTHETIC_CHAT_MESSAGE_ID_PREFIX
];
export function isReservedChatMessageId(id) {
    return RESERVED_CHAT_MESSAGE_ID_PREFIXES.some((prefix)=>id.startsWith(prefix));
}
/** Any message id, client- or server-minted: bounded, nothing else. */ export const ChatMessageIdSchema = z.string().min(1).max(MAX_CHAT_MESSAGE_ID_LENGTH);
/**
 * A turn key as a CLIENT may send it: bounded, and outside the server's own id
 * namespace (see {@link RESERVED_CHAT_MESSAGE_ID_PREFIXES}).
 */ export const ClientChatMessageIdSchema = ChatMessageIdSchema.refine((id)=>!isReservedChatMessageId(id), {
    message: 'Message id uses a reserved prefix'
});
/**
 * Ceiling on the serialized size of a single non-text part. Charts are small by
 * construction; the cap is enforced anyway so a producer bug cannot grow the
 * per-session document without bound. Text parts are exempt: they are the same
 * payload the pre-parts `content` field has always held unbounded, and dropping
 * an over-long one would silently lose an answer.
 */ export const MAX_CHAT_MESSAGE_PART_SERIALIZED_LENGTH = 16_384;
export const ChatTextPartSchema = z.object({
    type: z.literal('text'),
    text: z.string()
});
/**
 * The lifecycle of a tool part as the AI SDK streams it. Lives here, next to
 * the persisted narrowing, because the two are one concept: only the SETTLED
 * states are written (see {@link PERSISTED_CHAT_TOOL_PART_STATES}) — the
 * in-flight ones exist solely on the live stream. The transcript renderer
 * derives its streaming part types from the persisted ones by widening `state`
 * back to this union.
 */ export const CHAT_TOOL_PART_STATES = [
    'input-streaming',
    'input-available',
    'output-available',
    'output-error'
];
/**
 * The states a stored tool part may carry: the call SETTLED, either with an
 * output or with an error.
 *
 * `output-error` is persisted (plan §D7) because the alternative was a call
 * that never happened: the model saw the failure in-stream, the user saw a live
 * error card, and a reload showed neither — so nobody could reconstruct why a
 * write was not there. What makes it storable rather than merely allowed is
 * {@link MAX_CHAT_TOOL_ERROR_TEXT_LENGTH}: the message is the laundered
 * `SafeToolError` text, never a raw internal one.
 */ export const PERSISTED_CHAT_TOOL_PART_STATES = [
    'output-available',
    'output-error'
];
/**
 * Ceiling on a persisted tool error message, and the same cap the producer
 * applies before the model ever reads it — one bound for one string, so a
 * message the model was shown can never be one the transcript has to drop.
 */ export const MAX_CHAT_TOOL_ERROR_TEXT_LENGTH = 500;
/**
 * Named so a renamed state cannot silently disable the refines that read it.
 * `satisfies` rather than an annotation: the checked membership is the point,
 * and the literal type is what lets {@link settledToolPartFields} narrow on it.
 */ const FAILED_TOOL_PART_STATE = 'output-error';
/**
 * What a failed part renders as when the row carries no message at all — see
 * {@link settledToolPartFields}.
 */ const UNRECORDED_TOOL_FAILURE = 'The tool call failed.';
/**
 * The frame every persisted tool part shares: a call id, the settled state, and
 * an untyped `input`/`output` pair bounded by the part size budget. The TOOL is
 * the validation chokepoint for the payloads (plan 009 §2); this envelope only
 * guarantees the frame, the budget and the state/payload agreement, which is why
 * one factory can mint every tool part instead of each restating the same rules.
 *
 * The state/payload agreement is two halves of one rule — a FAILED call carries
 * its message and no output, a COMPLETED one carries no message — and it is two
 * refines so the rejection names the half that broke; a single predicate reports
 * the same sentence for two opposite mistakes.
 *
 * A cross-field rule rather than a `state`-discriminated union of two object
 * schemas, deliberately: the readers derive their own shapes from these
 * (`Omit<ChatRenderChartPart, 'state'>` in the transcript types, the `.shape`
 * field walk that keeps the Mongoose subschema aligned), and a union collapses
 * both to the fields the variants share — silently dropping `output` from the
 * renderer's part type. The runtime rule is what the write gate needs, and it is
 * exact.
 */ function toolPartSchema(type, extra) {
    return z.object({
        type: z.literal(type),
        toolCallId: z.string().min(1).max(MAX_CHAT_MESSAGE_ID_LENGTH),
        state: z.enum(PERSISTED_CHAT_TOOL_PART_STATES),
        input: z.unknown().optional(),
        output: z.unknown().optional(),
        /** Present exactly on an `output-error` part — see the refines below. */ errorText: z.string().min(1).max(MAX_CHAT_TOOL_ERROR_TEXT_LENGTH).optional(),
        ...extra ?? {}
    }).refine((part)=>part.state !== FAILED_TOOL_PART_STATE || part.errorText !== undefined && part.output === undefined, {
        message: `Tool part "${type}" in state "output-error" must carry an errorText and no output`
    }).refine((part)=>part.state === FAILED_TOOL_PART_STATE || part.errorText === undefined, {
        message: `Tool part "${type}" may only carry an errorText when it failed`
    }).refine(isWithinPartSizeBudget, {
        message: `Tool part "${type}" exceeds ${MAX_CHAT_MESSAGE_PART_SERIALIZED_LENGTH} serialized characters`
    });
}
export const ChatRenderChartPartSchema = toolPartSchema('tool-renderChart');
/**
 * A completed `generateDocument` call: the download card a reloaded session
 * re-renders.
 *
 * The referenced file is EPHEMERAL (retention ~24h) while the part is not: an
 * old turn keeps showing its card, and the card degrades to "expired" when the
 * download endpoint no longer finds the object. Storing the part is what makes
 * that honest — dropping it would silently erase that an export was made.
 */ export const ChatGenerateDocumentPartSchema = toolPartSchema('tool-generateDocument');
/**
 * A completed `renderEntityView` call: one of the product's own record surfaces
 * embedded in the transcript (plan 016 §1).
 *
 * Unlike the chart — whose numbers ARE the answer and are therefore frozen — the
 * payload here is `{view, ids}` and the component fetches on every render. That
 * is what makes an old transcript keep telling the truth: the sensitive-field
 * capability is re-derived per request, a rename or a soft-delete shows through,
 * and a reader who has lost access sees the unavailable card rather than a
 * label captured when the answer was written. Nothing stored here can outlive
 * that check, because nothing stored here identifies the record beyond its id.
 */ export const ChatRenderEntityViewPartSchema = toolPartSchema('tool-renderEntityView');
/**
 * Bound on a persisted app tool name — the same character set the app-chat
 * descriptor enforces (`AppChatToolDescriptorSchema.name`).
 */ export const MAX_CHAT_APP_TOOL_NAME_LENGTH = 64;
/**
 * A settled tool call of an APP chat kind (plan-2 §3) — one that produced an
 * output, or one that failed and carries its `errorText`.
 *
 * The platform's own rich parts are one persisted type per tool because the
 * renderer knows each one. An app's tools are declared by the app and unknown
 * to this package, and the AI SDK streams them as `tool-<name>` — one wire type
 * per tool, unbounded and unlistable. Persisting them under a SINGLE type with
 * the name as data is what keeps the closed part union closed: a new app tool
 * needs no contract change, and the reader still validates every stored part
 * against a strict schema.
 *
 * It is not decoration. Without it a tool-only assistant turn — the model calls
 * `mutatePlan`, writes the artifact and says nothing — projects to no
 * persistable parts at all, and the turn writer's "a turn with nothing to show
 * was not a turn" rule would then discard the user's question along with it.
 *
 * `input`/`output` are untyped and bounded by the shared part budget: the APP's
 * tool schema is the validation chokepoint (the worker parses arguments against
 * the real Zod schema), and this package cannot know the shape.
 */ export const ChatAppToolPartSchema = toolPartSchema('tool-app', {
    toolName: z.string().regex(new RegExp(`^[a-zA-Z0-9_-]{1,${MAX_CHAT_APP_TOOL_NAME_LENGTH}}$`), 'App tool names must be 1-64 characters of letters, digits, underscores, or hyphens')
});
/**
 * The settled half of a stored tool part, in the shape the AI SDK's part union
 * takes — what every reader that reloads a transcript into `useChat` spreads.
 *
 * A stored failure must stay a failure (plan §D7): rebuilt as an
 * `output-available` part with a missing output, it draws the tool's own "done"
 * or "no data" state for a write that never landed.
 *
 * The `errorText` an error part carries is guaranteed twice over — the refine
 * above refuses the part without one, and the producer clamps and defaults it
 * at the write (`toPersistableAppParts`) — but that is a CROSS-FIELD rule the
 * inferred type cannot express, so the branch below still has to name a value.
 * {@link UNRECORDED_TOOL_FAILURE} rather than `''`, because the only rows that
 * can reach it are ones this contract did not write (a legacy row, a rolled-back
 * node), and an empty string draws an error card that tells the reader nothing.
 */ export function settledToolPartFields(part) {
    return part.state === FAILED_TOOL_PART_STATE ? {
        state: part.state,
        errorText: part.errorText ?? UNRECORDED_TOOL_FAILURE
    } : {
        state: part.state,
        output: part.output
    };
}
/**
 * A document the user attached to a turn and the model read (plan 012 §2).
 *
 * The BYTES ARE NOT HERE and never will be: the part stores the storage key our
 * presign endpoint minted, so Mongo holds a handle while the object store holds
 * the multi-megabyte body. A persisted chat document that inlined base64 would
 * grow the session document without bound and would put untrusted user bytes in
 * every read path that touches a message.
 *
 * `storageKey` is constrained to the key shapes we mint rather than to any
 * string: a persisted part can then only ever point at an object of ours, so a
 * corrupted or hand-written row cannot make a later replay fetch something
 * arbitrary. BOTH chat surfaces store their handles here — the part is one
 * transcript shape read by one reader — so the surface namespaces are separated
 * where it matters instead, at the read that turns a key into bytes. The backend
 * still re-derives ownership from the caller's claims.
 *
 * Like the export part, the file it names is EPHEMERAL while the part is not —
 * after the retention window the object is swept, the part remains, and it
 * renders as an inert "expired" chip that is omitted from model input.
 */ export const ChatFilePartSchema = z.object({
    type: z.literal('file'),
    storageKey: z.string().regex(STORED_CHAT_UPLOAD_KEY_REGEX, 'Invalid chat upload key'),
    filename: z.string().min(1).max(MAX_CHAT_ATTACHMENT_FILENAME_LENGTH),
    mediaType: z.enum(CHAT_UPLOAD_MEDIA_TYPES)
});
function isWithinPartSizeBudget(part) {
    try {
        return JSON.stringify(part ?? null).length <= MAX_CHAT_MESSAGE_PART_SERIALIZED_LENGTH;
    } catch  {
        // Unserializable (cycles, BigInt) — it could never be persisted anyway.
        return false;
    }
}
/**
 * Recognized part types, keyed by their discriminator. Readers validate each
 * part individually against this map and skip anything absent from it: a strict
 * union over the whole array would reject an entire message because one future
 * part type was unknown (plan 009 §6.4).
 */ export const CHAT_MESSAGE_PART_SCHEMAS = {
    text: ChatTextPartSchema,
    file: ChatFilePartSchema,
    'tool-renderChart': ChatRenderChartPartSchema,
    'tool-generateDocument': ChatGenerateDocumentPartSchema,
    'tool-renderEntityView': ChatRenderEntityViewPartSchema,
    'tool-app': ChatAppToolPartSchema
};
export const ChatMessagePartSchema = z.discriminatedUnion('type', [
    ChatTextPartSchema,
    ChatFilePartSchema,
    ChatRenderChartPartSchema,
    ChatGenerateDocumentPartSchema,
    ChatRenderEntityViewPartSchema,
    ChatAppToolPartSchema
]);
/**
 * Ceiling on a thumbs-down comment. One constant because the same text is
 * bounded three times — the request body, the stored subdocument and the
 * Langfuse score comment — and three independent caps would disagree on the
 * boundary that rejects a comment the user already typed.
 */ export const MAX_AI_FEEDBACK_COMMENT_LENGTH = 1000;
/**
 * A reader's thumbs verdict on one assistant answer.
 *
 * A discriminated union rather than `{value, comment?}` because "free text
 * belongs to a thumbs-DOWN" is an invariant, not a convention (owner ruling: no
 * comment on praise): the up branch has no comment key at all, so request,
 * storage and response cannot disagree about it, and a commented up-vote is
 * REJECTED rather than silently stripped into a state the caller never sent.
 *
 * A blank comment is rejected outright — "commented with nothing" and "not
 * commented" must not be two states in storage. Trimming a blank submission to
 * absent belongs to the service that accepts the raw textarea value.
 *
 * The branches are deliberately NON-strict: the history read path parses stored
 * messages that were never `lean()`ed, and a hydrated Mongoose subdocument
 * enumerates the driver's own internals. A strict object would reject every
 * real read and, behind the normalizer's `.catch`, erase the vote.
 */ const AiFeedbackCommentSchema = z.string().max(MAX_AI_FEEDBACK_COMMENT_LENGTH).refine((comment)=>comment.trim().length > 0, {
    message: 'Comment must contain more than whitespace'
});
/** The closed verdict vocabulary — request enum, union discriminators and the
 *  Mongoose prop enum all derive from this one list. */ export const CHAT_MESSAGE_FEEDBACK_VALUES = [
    'up',
    'down'
];
const [FEEDBACK_UP, FEEDBACK_DOWN] = CHAT_MESSAGE_FEEDBACK_VALUES;
export const ChatMessageUserFeedbackSchema = z.discriminatedUnion('value', [
    z.object({
        value: z.literal(FEEDBACK_UP),
        comment: z.undefined().optional()
    }),
    z.object({
        value: z.literal(FEEDBACK_DOWN),
        comment: AiFeedbackCommentSchema.optional()
    })
]);
/**
 * A chat message as the history API returns it and as the renderer walks it.
 *
 * `entityRefs`, `langfuseTraceId`, `aiProvenance` and `userFeedback` are
 * message-level METADATA, not parts — they describe the turn (its sources, its
 * Langfuse trace, its AI mark, the reader's verdict on it), not a renderable
 * block, and a parts rewrite that folded them in would strand them.
 *
 * `userFeedback` is absent until somebody votes and absent again once the vote
 * is retracted: like the AI mark, its PRESENCE is the statement. The Bliksy and
 * app-chat feedback endpoints write it. On a shared Bliksy recording the vote is
 * session-level: recorder and manager overwrite the same field, last writer
 * wins.
 *
 * `aiProvenance` is present only when a model generated the message: a
 * deterministic assistant message (a templated greeting, an attachment handoff)
 * and every message written before the mark existed carry none.
 *
 * `createdAt` is optional and never fabricated: the migrated Omniscient rows
 * carry it only sometimes, and Bliksy has no per-message timestamp at all (only
 * generation-level).
 */ export const ChatMessageEnvelopeSchema = z.object({
    id: ChatMessageIdSchema,
    role: ChatMessageRoleSchema,
    schemaVersion: z.number().int().min(0),
    createdAt: z.iso.datetime().optional(),
    parts: z.array(ChatMessagePartSchema),
    entityRefs: z.array(ChatEntityRefSchema).optional(),
    langfuseTraceId: z.string().optional(),
    aiProvenance: AiProvenanceSchema.optional(),
    userFeedback: ChatMessageUserFeedbackSchema.optional()
});
/**
 * Message metadata a chat SSE stream publishes. Modelled here because the AI
 * SDK types stream metadata as an opaque bag — a hand-written interface on one
 * side of it drifts from the server silently. Bliksy uses this shape directly;
 * app chat extends it with `sessionId`.
 *
 * Every field is optional because each rides out only when there is something
 * to say — `langfuseTraceId` exists only while telemetry is on, and
 * `aiProvenance` only on messages a model generated.
 */ export const ChatStreamMetadataSchema = z.object({
    langfuseTraceId: z.string().optional(),
    aiProvenance: AiProvenanceSchema.optional(),
    /**
   * When the answer finished streaming, so a live turn can show a timestamp
   * before any reload. A fact of the stream, not of storage — the stored
   * envelope stamps its own rows — and never fabricated for a turn that
   * predates it.
   */ createdAt: z.iso.datetime().optional()
});

//# sourceMappingURL=chat-message.js.map